// Geocoding de fábricas para la vista de mapa de Estadísticas.
//
// Reglas de funcionamiento (acordadas con el equipo):
//
//   1. Cache persistente en IndexedDB (store factoryGeocodes). La primera
//      vez que vemos una fábrica, geocodificamos; las siguientes salen del
//      cache instantáneo. Tanto los hits como los misses se persisten — los
//      misses con un TTL para reintentar pasado un tiempo prudencial.
//
//   2. NO bloqueante. La página renderiza inmediatamente; los marcadores van
//      apareciendo a medida que cada fábrica resuelve. Los suscriptores se
//      enteran vía un patrón observable (subscribeFactoryGeocodes / emit).
//
//   3. Una sola petición por fábrica única. Deduplicación de in-flight, así
//      si dos componentes piden la misma fábrica al mismo tiempo, comparten
//      la misma promesa.
//
//   4. Cola rate-limited (1 req/seg) — para no abusar de la API gratuita
//      de OpenMeteo Geocoding (la misma que ya usamos en el clima). Si un
//      día migramos a Azure Maps o Google podemos subir el ratio.
//
//   5. Reutiliza la lógica buildCandidates() del clima: limpia
//      "(controller@email.com)" del final, normaliza, prioriza el último
//      token (que suele ser el país/ciudad significativa) y prueba varias
//      variantes hasta acertar.

import {
  getCachedFactoryGeocode,
  putFactoryGeocode,
  deleteFactoryGeocode,
  clearAllFactoryGeocodes,
  type FactoryGeocode,
} from './db';
import { geocodeSearchApi, type GeocodeSearchResult } from './api';
import { isOfflineForUser } from './networkStatus';

// ── Constantes ──────────────────────────────────────────────────────────

// Rate limit dinámico: si tenemos clave de Azure Maps usamos un ritmo
// rápido (Azure permite ~50 req/seg, dejamos margen y bajamos a ~7); si
// no, caemos al ritmo lento de Nominatim (1 req/seg requerido por su
// política de uso). Lo determinamos al construir la cola.
const RATE_LIMIT_AZURE_MS = 150;       // ~6-7 req/seg (Azure permite mucho más)
const RATE_LIMIT_FREE_MS = 1100;       // ~1 req/seg (Nominatim/OpenMeteo gratis)
const HTTP_TIMEOUT_MS = 6_000;

// Azure Maps se consulta a través de NUESTRA API, así que el cliente ya no
// necesita (ni debe tener) la clave. No podemos saber de antemano si el
// servidor la tiene configurada: lo intentamos y, si responde que no lo está,
// lo recordamos para el resto de la sesión y no volvemos a insistir. El
// fallback a Nominatim/OpenMeteo sigue funcionando igual que antes.
let azureGeocoderAvailable = true;
// Ritmo dinámico: mientras la geocodificación va por nuestra API (Azure) se
// puede ir rápido; en cuanto caemos a Nominatim/OpenMeteo hay que bajar a
// ~1 req/seg, que es lo que exige su política de uso — pasarse puede acabar
// en bloqueo de IP.
function rateLimitMs(): number {
  return azureGeocoderAvailable ? RATE_LIMIT_AZURE_MS : RATE_LIMIT_FREE_MS;
}

const NOISE_WORDS = new Set([
  's/n', 'sn', 'calle', 'avenida', 'av', 'num', 'nro', 'no', 'polig',
  'poligono', 'pol', 'pol.', 'industrial', 'de', 'del', 'la', 'el',
  'los', 'las', 'y', 'con', 'sin',
]);

// Mapeo nombre de país → código ISO 3166-1 alpha-2 para sesgar las
// búsquedas en Nominatim/OpenMeteo. Sin esto, Nominatim devuelve la
// primera coincidencia mundial — y "Bujaraloz" o "Almuniente" pueden
// resolver a una localidad homónima en México u otro país.
//
// Añade aquí cualquier país adicional donde Aldahra opere. Tanto la
// versión con tilde como sin ella se mapean.
const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  'españa': 'es', 'espana': 'es', 'spain': 'es',
  'argentina': 'ar',
  'portugal': 'pt',
  'francia': 'fr', 'france': 'fr',
  'italia': 'it', 'italy': 'it',
  'marruecos': 'ma', 'maroc': 'ma', 'morocco': 'ma',
  'mexico': 'mx', 'méxico': 'mx',
  'estados unidos': 'us', 'usa': 'us', 'united states': 'us',
  'reino unido': 'gb', 'united kingdom': 'gb', 'uk': 'gb',
  'alemania': 'de', 'germany': 'de', 'deutschland': 'de',
  'paises bajos': 'nl', 'países bajos': 'nl', 'holanda': 'nl', 'netherlands': 'nl',
  'belgica': 'be', 'bélgica': 'be', 'belgium': 'be',
  'polonia': 'pl', 'poland': 'pl',
  'turquia': 'tr', 'turquía': 'tr', 'turkey': 'tr',
};

// País por defecto cuando la dirección no menciona ninguno explícitamente.
// La mayoría de fábricas de Aldahra están en España, así que sesgamos a
// "es" para evitar falsos positivos en localidades homónimas (ej.
// "Bujaraloz" existe en Monterrey, México). Si Aldahra abre en un mercado
// nuevo y la dirección no incluye país, conviene actualizar el JSON de
// direcciones para incluirlo, o mover este default a una preferencia.
const DEFAULT_COUNTRY_CODE = 'es';

function extractCountryCode(raw: string): string | null {
  if (!raw) return DEFAULT_COUNTRY_CODE;
  const lower = raw.toLowerCase();
  // Buscamos las claves más largas primero para que "estados unidos"
  // gane a un hipotético "estados".
  const keys = Object.keys(COUNTRY_NAME_TO_CODE).sort((a, b) => b.length - a.length);
  for (const name of keys) {
    if (lower.includes(name)) return COUNTRY_NAME_TO_CODE[name];
  }
  // Sin coincidencia explícita: usamos el país por defecto.
  return DEFAULT_COUNTRY_CODE;
}

// ── Tipos ──────────────────────────────────────────────────────────────

export interface FactoryCoords {
  lat: number;
  lng: number;
  country?: string;
}

// ── Estado en memoria ──────────────────────────────────────────────────

// Tabla de coords resueltas (espejo del cache persistente, para lectura
// inmediata sin tocar IndexedDB en cada render).
const memCache = new Map<string, FactoryCoords | null>();
let memHydrated = false;

// Promesas en vuelo, para deduplicar peticiones simultáneas.
const inFlight = new Map<string, Promise<FactoryCoords | null>>();

// Cola serial a nivel de PETICIÓN HTTP (no de fábrica): cada call al
// geocoder espera al menos rateLimitMs() desde el anterior — así no
// importa que múltiples fábricas o múltiples candidatos se disparen a la
// vez, todos van por la misma garganta serializada que respeta la política
// de Nominatim (1 req/seg).
let httpChain: Promise<unknown> = Promise.resolve();
let lastHttpAt = 0;

// Suscriptores: el componente Map se entera cuando cambia el estado para
// pintar nuevos marcadores. Patrón observable como notifications.ts.
type Listener = () => void;
const listeners = new Set<Listener>();
function emit() { listeners.forEach((l) => l()); }

// ── API pública ────────────────────────────────────────────────────────

function key(factoryName: string): string {
  return (factoryName ?? '').trim().toLowerCase();
}

/**
 * Hidrata el cache en memoria desde IndexedDB. Idempotente — solo carga
 * la primera vez. El componente Map lo llama al montarse para tener los
 * pintados disponibles instantáneamente.
 */
export async function hydrateFactoryGeocodes(factoryNames: string[]): Promise<void> {
  if (memHydrated) return;
  await Promise.all(factoryNames.map(async (name) => {
    const k = key(name);
    if (memCache.has(k)) return;
    const cached = await getCachedFactoryGeocode(name);
    if (!cached) return;
    if (cached.lat == null || cached.lng == null) {
      // Miss persistido (legado de versiones anteriores): lo borramos de
      // BD para que se reintente con el geocoder actual. Ya no persistimos
      // misses en absoluto a partir de ahora.
      void deleteFactoryGeocode(name);
    } else {
      memCache.set(k, { lat: cached.lat, lng: cached.lng, country: cached.country ?? undefined });
    }
  }));
  memHydrated = true;
  emit();
}

/**
 * Devuelve las coords actuales de una fábrica si están resueltas. No
 * dispara fetch — solo lectura. Si no están en cache devuelve undefined
 * (todavía no resuelto) o null (resuelto como miss).
 */
export function getFactoryCoords(factoryName: string): FactoryCoords | null | undefined {
  const k = key(factoryName);
  if (!memCache.has(k)) return undefined;
  return memCache.get(k) ?? null;
}

/**
 * Programa la geocodificación de una lista de fábricas. Para cada una:
 *   - Si ya está resuelta (hit o miss vigente) → no hace nada.
 *   - Si no, encola un lookup contra OpenMeteo respetando rate limit.
 * Devuelve cuando todas las pendientes han terminado, pero los marcadores
 * van apareciendo conforme cada una resuelve (vía emit()).
 */
export async function ensureFactoryGeocodes(addresses: Array<{ factoryName: string; factoryAdress?: string }>): Promise<void> {
  if (isOfflineForUser()) return; // sin red: solo lo que ya esté en cache
  const promises: Array<Promise<unknown>> = [];
  for (const { factoryName, factoryAdress } of addresses) {
    if (!factoryName) continue;
    const k = key(factoryName);
    if (memCache.has(k)) continue;            // ya resuelto (hit o miss)
    if (inFlight.has(k)) { promises.push(inFlight.get(k)!); continue; }

    const p = doGeocode(factoryName, factoryAdress);
    inFlight.set(k, p);
    p.finally(() => inFlight.delete(k));
    promises.push(p);
  }
  await Promise.all(promises);
}

export function subscribeFactoryGeocodes(cb: Listener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * Borra todo lo cacheado: memoria + IndexedDB. Útil cuando el usuario
 * fuerza un refresh manual (pull-to-refresh) y quiere reintentar el
 * geocoding desde cero. NO cancela peticiones en vuelo — esas terminarán
 * y se volverán a guardar; pero a partir de la siguiente llamada a
 * ensureFactoryGeocodes todas las fábricas se reintentan.
 *
 * IMPORTANTE: si el usuario está offline (sin red o modo offline manual),
 * la llamada es un no-op — no tendría sentido borrar lo único que el user
 * podría ver (los marcadores cacheados) si no podemos volver a
 * geocodificar. Así, un refresh accidental estando sin datos preserva la
 * información existente.
 */
export async function resetFactoryGeocodes(): Promise<void> {
  if (isOfflineForUser()) return;
  memCache.clear();
  memHydrated = false;
  await clearAllFactoryGeocodes();
  emit();
}

// ── Implementación ─────────────────────────────────────────────────────

async function doGeocode(factoryName: string, factoryAdress?: string): Promise<FactoryCoords | null> {
  const k = key(factoryName);

  // Re-check cache persistente (puede haber resuelto mientras esperábamos
  // en cola si otro caller ya lo geocodeó por otro path).
  const persisted = await getCachedFactoryGeocode(factoryName);
  if (persisted && persisted.lat != null && persisted.lng != null) {
    const coords = { lat: persisted.lat, lng: persisted.lng, country: persisted.country ?? undefined };
    memCache.set(k, coords);
    emit();
    return coords;
  }
  // Si hay una entrada legado con null/null (versiones anteriores que
  // persistían misses), la borramos para no contaminar.
  if (persisted && (persisted.lat == null || persisted.lng == null)) {
    void deleteFactoryGeocode(factoryName);
  }

  // Construye candidatos a partir de la dirección bruta. Si no hay
  // factoryAdress, probamos solo con el factoryName.
  const candidates = buildCandidates(factoryAdress ?? factoryName);
  // Plus: factoryName solo, por si la dirección es ambigua y el nombre
  // de la fábrica es más reconocible (ej: "MEGAFARDOS Argentina").
  const fName = factoryName.trim().toLowerCase();
  if (fName && !candidates.includes(fName)) candidates.push(fName);

  // Sesgo por país. Preferimos el país del parseAddress (extracción
  // estructurada: el último segmento contiene "CP PAÍS"), y si no hay
  // dirección, hacemos scan de la cadena entera. Si no se detecta nada,
  // el extractCountryCode cae en el default ('es').
  const parsed = parseAddress(factoryAdress ?? '');
  const countryCode = parsed.countryName
    ? (COUNTRY_NAME_TO_CODE[parsed.countryName.toLowerCase()] ?? extractCountryCode(factoryAdress ?? factoryName))
    : extractCountryCode(factoryAdress ?? factoryName);

  for (const cand of candidates) {
    // Cadena de geocoders por orden de prioridad:
    //   1) Azure Maps (rápido, ~50 req/seg, gratuito hasta 5k/día)
    //      → primario si hay clave configurada.
    //   2) Nominatim (gratis, 1 req/seg, texto libre).
    //   3) OpenMeteo Geocoding (gratis, ciudades conocidas).
    // Cualquier fallo cae al siguiente. Cuando Azure responde, nos
    // ahorramos ~95% del tiempo respecto a la versión 100% gratuita.
    let got: FactoryCoords | null = null;
    if (azureGeocoderAvailable) {
      got = await azureMapsGeocode(cand, countryCode);
    }
    if (!got) got = await nominatimGeocode(cand, countryCode);
    if (!got) got = await openMeteoGeocode(cand, countryCode);
    if (got) {
      memCache.set(k, got);
      const rec: FactoryGeocode = {
        factoryName,
        lat: got.lat,
        lng: got.lng,
        country: got.country ?? null,
        geocodedAt: Date.now(),
      };
      // No bloqueamos el flujo del emit() esperando a la persistencia.
      void putFactoryGeocode(rec);
      emit();
      return got;
    }
  }
  // Si todos los candidatos fallaron, log mínimo para depurar (visible en
  // DevTools del cliente). No es ruidoso porque solo ocurre la primera vez
  // que vemos una fábrica que no resuelve.
  // eslint-disable-next-line no-console
  console.warn('[factoryGeocoding] Sin coords:', factoryName, '→ candidatos:', candidates);

  // Miss: NO persistimos en BD. Solo memoria — así reintenta en la próxima
  // visita en lugar de quedar bloqueado por el cache hasta MISS_TTL_MS.
  // Si la fábrica realmente nunca se geocodifica, el coste es irrelevante:
  // 1 req/seg adicional por sesión.
  memCache.set(k, null);
  emit();
  return null;
}

// Resultado del parser estructurado de una dirección Aldahra.
interface ParsedAddress {
  city: string | null;
  postalCode: string | null;
  countryName: string | null;
}

// Detecta automáticamente el patrón típico de las direcciones Aldahra:
//   "<calle/ctra/apdo> - <CIUDAD> - <CP PAÍS>"
// El parser ignora todo el primer segmento (la calle, que suele ser ruido
// para geocoders) y se queda con la ciudad y el código postal + país, que
// son los datos verdaderamente discriminantes. El CP en España es único
// por municipio, así que Nominatim resuelve en el primer intento.
function parseAddress(raw: string): ParsedAddress {
  const cleaned = (raw ?? '').trim().replace(/\s*\([^)]*\)\s*$/, '').trim();
  if (!cleaned) return { city: null, postalCode: null, countryName: null };

  const parts = cleaned.split(/\s*-\s*/).map((s) => s.trim()).filter(Boolean);

  // Buscar el segmento que contiene "CP PAÍS". Lo recorremos desde el
  // final porque las direcciones Aldahra ponen el código postal y el país
  // siempre como último segmento. Si una dirección es atípica y el CP
  // está en mitad, igualmente lo encontraremos.
  let cpIndex = -1;
  let postalCode: string | null = null;
  let countryName: string | null = null;
  for (let i = parts.length - 1; i >= 0; i--) {
    const m = parts[i].match(/^\s*(\d{4,5})\s+(.+?)\s*$/);
    if (m) {
      cpIndex = i;
      postalCode = m[1];
      countryName = m[2].trim();
      break;
    }
  }

  // La ciudad suele ser el segmento inmediatamente anterior al de CP+país.
  // Saneamos un par de cosas: comas finales, "S/N" suelto, etc.
  let city: string | null = null;
  if (cpIndex > 0) {
    let candidate = parts[cpIndex - 1];
    candidate = candidate.replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
    // Si el candidato es solo un código postal (otro), o "s/n", o vacío,
    // miramos un segmento más atrás como último intento.
    const looksUseful = candidate.length >= 2 && !/^\s*\d+\s*$/.test(candidate)
      && !/^s\s*\/?\s*n\s*$/i.test(candidate);
    if (looksUseful) city = candidate;
    else if (cpIndex > 1) city = parts[cpIndex - 2].replace(/,/g, ' ').trim() || null;
  }

  return { city, postalCode, countryName };
}

const norm = (s: string) =>
  s.toLowerCase().replace(/[,.;]+/g, ' ').replace(/\s+/g, ' ').trim();

function buildCandidates(raw: string): string[] {
  if (!raw) return [];
  const cleaned = raw.trim().replace(/\s*\([^)]*\)\s*$/, '').trim();
  const parsed = parseAddress(raw);
  const out: string[] = [];

  // ── Candidatos de máxima precisión ───────────────────────────────────
  // El código postal en España (y la mayoría de países) es único por
  // municipio, así que "ciudad CP país" o incluso "CP país" resuelven a
  // la coordenada exacta del centro del CP. Estos son nuestros mejores
  // candidatos y por eso van primero.
  if (parsed.city && parsed.postalCode && parsed.countryName) {
    out.push(norm(`${parsed.city} ${parsed.postalCode} ${parsed.countryName}`));
  }
  if (parsed.postalCode && parsed.countryName) {
    out.push(norm(`${parsed.postalCode} ${parsed.countryName}`));
  }
  if (parsed.city && parsed.countryName) {
    out.push(norm(`${parsed.city} ${parsed.countryName}`));
  }
  if (parsed.city) {
    out.push(norm(parsed.city));
  }

  // ── Fallback robusto ────────────────────────────────────────────────
  // Si no detectamos el patrón con guiones (direcciones atípicas),
  // intentamos con tokens significativos sin ruido — la lógica que ya
  // teníamos antes. Solo se llega aquí si el parseAddress no extrajo
  // nada útil.
  if (out.length === 0) {
    const symbolless = cleaned.toLowerCase().replace(/[0-9.,;:_\-/\\#·"'`*+!?¿¡]/g, ' ');
    const tokens = symbolless.split(/\s+/).filter((t) => {
      if (!t || t.length < 3) return false;
      return !NOISE_WORDS.has(t);
    });
    if (tokens.length > 0) out.push(tokens.join(' '));
    if (tokens.length >= 2) out.push(tokens.slice(-2).join(' '));
    if (tokens.length >= 1) out.push(tokens[tokens.length - 1]);
  }

  return Array.from(new Set(out.filter(Boolean)));
}

async function azureMapsGeocode(query: string, countryCode: string | null = null): Promise<FactoryCoords | null> {
  // Azure Maps a través de NUESTRA API, no desde el navegador.
  //
  // Antes se llamaba a atlas.microsoft.com directamente con la clave incrustada
  // en el bundle. La cuenta de Azure exige lista blanca de orígenes CORS, así que
  // cualquier dominio o puerto no listado recibía 403: esta función devolvía null
  // SIEMPRE y el mapa caía en silencio al geocodificador gratuito. Con el
  // servidor de por medio no hay cabecera Origin y la clave no sale de él.
  //
  // Si la API falla, se devuelve null y el caller sigue con su cadena de
  // respaldos — el comportamiento degradado se conserva intacto.
  if (!azureGeocoderAvailable) return null;
  let data: GeocodeSearchResult | null = null;
  try {
    const res = await geocodeSearchApi(query, countryCode ?? undefined, 'es-ES');
    data = res.data ?? null;
  } catch (e) {
    // 503 = el servidor no tiene clave configurada. No tiene sentido seguir
    // preguntando por cada fábrica: se desactiva para el resto de la sesión.
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 503) azureGeocoderAvailable = false;
    return null;
  }
  const lat = data?.latitude;
  const lon = data?.longitude;
  if (typeof lat !== 'number' || typeof lon !== 'number') return null;
  const first = { address: { country: data?.country ?? undefined } };
  return {
    lat,
    lng: lon,
    country: first?.address?.country,
  };
}

async function nominatimGeocode(query: string, countryCode: string | null = null): Promise<FactoryCoords | null> {
  // Nominatim de OSM: acepta búsqueda libre. Lo usamos vía la API pública
  // (gratis, sin key). Su política recomienda 1 req/seg — la cola de este
  // servicio ya garantiza ese ritmo. Si llega countryCode, lo pasamos
  // como filtro para evitar coincidencias homónimas en otros países.
  const cc = countryCode ? `&countrycodes=${countryCode}` : '';
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&addressdetails=1${cc}&q=${encodeURIComponent(query)}`;
  const json = await fetchJson(url);
  if (!json) return null;
  const arr = json as Array<{
    lat?: string; lon?: string;
    address?: { country?: string };
  }>;
  const first = arr?.[0];
  if (!first || !first.lat || !first.lon) return null;
  const lat = parseFloat(first.lat);
  const lng = parseFloat(first.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng, country: first.address?.country };
}

async function openMeteoGeocode(query: string, countryCode: string | null = null): Promise<FactoryCoords | null> {
  // OpenMeteo soporta country como ISO alpha-2 también. Si llega, le
  // damos prioridad a resultados del país detectado.
  const cc = countryCode ? `&country=${countryCode.toUpperCase()}` : '';
  // Pedimos varios resultados para poder filtrar por país si OpenMeteo no
  // respeta del todo el filtro (a veces devuelve resultados de fuera).
  const url = `https://geocoding-api.open-meteo.com/v1/search?count=5&language=es${cc}&name=${encodeURIComponent(query)}`;
  const json = await fetchJson(url);
  if (!json) return null;
  const arr = (json as { results?: Array<{ latitude?: number; longitude?: number; country?: string; country_code?: string }> }).results;
  if (!arr || arr.length === 0) return null;
  // Si pedimos un país específico, nos quedamos con la primera coincidencia
  // de ese país; si no, con la primera global.
  const wantCC = countryCode?.toUpperCase();
  const pick = wantCC
    ? (arr.find((r) => r.country_code?.toUpperCase() === wantCC) ?? null)
    : arr[0];
  if (!pick || typeof pick.latitude !== 'number' || typeof pick.longitude !== 'number') {
    return null;
  }
  return { lat: pick.latitude, lng: pick.longitude, country: pick.country };
}

// fetch real, sin rate limit (interno).
async function fetchJsonRaw(url: string): Promise<unknown> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), HTTP_TIMEOUT_MS);
  try {
    const r = await fetch(url, { signal: ctrl.signal });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// fetch público: encadena en httpChain y respeta rateLimitMs() entre
// peticiones. Garantiza que aunque varias fábricas pidan a la vez, el
// geocoder solo recibe una llamada por segundo aproximadamente.
async function fetchJson(url: string): Promise<unknown> {
  const ticket = httpChain.then(async () => {
    const wait = lastHttpAt + rateLimitMs() - Date.now();
    if (wait > 0) await new Promise((r) => setTimeout(r, wait));
    lastHttpAt = Date.now();
    return fetchJsonRaw(url);
  });
  httpChain = ticket.catch(() => undefined);
  return ticket;
}

